import { useState, useEffect, useCallback } from 'react';
import type { Flow, Etapa, ConfiguracaoCardConectado, CardConectadoRelacao } from '../../types';
import { useToast } from '../../contexts/ToastContext';
import { apiUrl } from '../../utils/apiBase';
import { 
  LinkIcon, 
  PlusIcon, 
  TrashIcon, 
  PencilIcon,
  ArrowRightIcon,
  EyeIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  DocumentDuplicateIcon,
  ClipboardDocumentListIcon,
  Cog6ToothIcon,
  ArrowsRightLeftIcon,
  CheckCircleIcon,
  XCircleIcon,
  FunnelIcon
} from '@heroicons/react/24/outline';

interface CardsConectadosProps {
  flows: Flow[];
  session?: any;
  userPermissions?: {
    canEdit: boolean;
    canAdmin: boolean;
    currentUser?: any;
  };
  onNavigateToFlow?: (flow: Flow) => void;
}

const CORES_DESTAQUE = [
  { valor: '#8B5CF6', nome: 'Roxo', classe: 'bg-purple-500' },
  { valor: '#3B82F6', nome: 'Azul', classe: 'bg-blue-500' },
  { valor: '#10B981', nome: 'Verde', classe: 'bg-emerald-500' },
  { valor: '#F59E0B', nome: 'Amarelo', classe: 'bg-amber-500' },
  { valor: '#EF4444', nome: 'Vermelho', classe: 'bg-red-500' },
  { valor: '#EC4899', nome: 'Rosa', classe: 'bg-pink-500' },
  { valor: '#06B6D4', nome: 'Ciano', classe: 'bg-cyan-500' },
  { valor: '#F97316', nome: 'Laranja', classe: 'bg-orange-500' },
];

const CardsConectados = ({ flows, session, userPermissions: _userPermissions, onNavigateToFlow: _onNavigateToFlow }: CardsConectadosProps) => {
  const { success, error: toastError } = useToast();
  
  // State
  const [configuracoes, setConfiguracoes] = useState<ConfiguracaoCardConectado[]>([]);
  const [relacoes, setRelacoes] = useState<CardConectadoRelacao[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'configuracoes' | 'relacoes' | 'visao'>('configuracoes');
  const [showForm, setShowForm] = useState(false);
  const [editingConfig, setEditingConfig] = useState<ConfiguracaoCardConectado | null>(null);
  const [expandedRelacao, setExpandedRelacao] = useState<string | null>(null);
  
  // Form state
  const [formData, setFormData] = useState({
    nome: '',
    descricao: '',
    tipo: 'criar' as 'criar' | 'mover_existente',
    fluxoOrigemId: '',
    etapaOrigemId: '',
    fluxoDestinoId: '',
    etapaDestinoId: '',
    copiarHistorico: true,
    copiarAnexos: true,
    corDestaque: '#8B5CF6',
    prefixoTitulo: '[CONECTADO]',
    obrigarPreenchimento: false,
    mapeamentoCampos: [] as { campoOrigemId: string; campoDestinoId: string }[],
    condicoes: [] as { campo: string; operador: string; valor: string }[],
    condicoesLogica: 'E' as 'E' | 'OU',
    ativo: true
  });

  const getAuthParams = useCallback(() => {
    const params = new URLSearchParams();
    if (session?.usuario?.email) params.set('userEmail', session.usuario.email);
    if (session?.empresa?.id) params.set('empresaId', session.empresa.id);
    return params.toString();
  }, [session]);

  const getAuthHeaders = useCallback(() => {
    return { 'Content-Type': 'application/json' };
  }, []);

  // Carregar dados
  const carregarDados = useCallback(async () => {
    try {
      setLoading(true);
      const headers = getAuthHeaders();
      
      const [configRes, relacoesRes] = await Promise.all([
        fetch(`${apiUrl('/cards-conectados/configuracoes')}?${getAuthParams()}`, { headers }),
        fetch(`${apiUrl('/cards-conectados/relacoes')}?${getAuthParams()}`, { headers })
      ]);
      
      const configData = await configRes.json();
      const relacoesData = await relacoesRes.json();
      
      if (configData.success) setConfiguracoes(configData.configuracoes || []);
      if (relacoesData.success) setRelacoes(relacoesData.relacoes || []);
    } catch (err) {
      console.error('Erro ao carregar dados de cards conectados:', err);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    carregarDados();
  }, [carregarDados]);

  // Helpers
  const getFlowNome = (flowId: string) => {
    const flow = flows.find(f => f.id === flowId);
    return flow?.name || 'Fluxo não encontrado';
  };

  const getEtapasDoFluxo = (flowId: string): Etapa[] => {
    const flow = flows.find(f => f.id === flowId);
    return flow?.etapas || [];
  };

  const getEtapaNome = (flowId: string, etapaId: string) => {
    const etapas = getEtapasDoFluxo(flowId);
    const etapa = etapas.find(e => e.id === etapaId);
    return etapa?.nome || 'Etapa não encontrada';
  };

  // CRUD
  const handleSalvarConfig = async () => {
    if (!formData.nome || !formData.fluxoOrigemId || !formData.etapaOrigemId || 
        !formData.fluxoDestinoId || !formData.etapaDestinoId) {
      toastError('Preencha todos os campos obrigatórios');
      return;
    }

    try {
      const headers = getAuthHeaders();
      const url = editingConfig 
        ? `${apiUrl(`/cards-conectados/configuracoes/${editingConfig.id}`)}?${getAuthParams()}`
        : `${apiUrl('/cards-conectados/configuracoes')}?${getAuthParams()}`;
      
      const res = await fetch(url, {
        method: editingConfig ? 'PUT' : 'POST',
        headers,
        body: JSON.stringify(formData)
      });
      
      const data = await res.json();
      if (data.success) {
        success(editingConfig ? 'Configuração atualizada!' : 'Configuração criada!');
        resetForm();
        carregarDados();
      } else {
        toastError(data.error || 'Erro ao salvar');
      }
    } catch (err) {
      toastError('Erro ao salvar configuração');
    }
  };

  const handleDeletarConfig = async (configId: string) => {
    if (!confirm('Tem certeza que deseja excluir esta configuração?')) return;
    
    try {
      const headers = getAuthHeaders();
      const res = await fetch(`${apiUrl(`/cards-conectados/configuracoes/${configId}`)}?${getAuthParams()}`, {
        method: 'DELETE',
        headers
      });
      
      const data = await res.json();
      if (data.success) {
        success('Configuração excluída!');
        carregarDados();
      } else {
        toastError(data.error || 'Erro ao excluir');
      }
    } catch (err) {
      toastError('Erro ao excluir configuração');
    }
  };

  const handleToggleConfig = async (configId: string) => {
    try {
      const headers = getAuthHeaders();
      const res = await fetch(`${apiUrl(`/cards-conectados/configuracoes/${configId}/toggle`)}?${getAuthParams()}`, {
        method: 'POST',
        headers
      });
      
      const data = await res.json();
      if (data.success) {
        success(data.message);
        carregarDados();
      }
    } catch (err) {
      toastError('Erro ao alterar status');
    }
  };

  const handleDeletarRelacao = async (relacaoId: string) => {
    if (!confirm('Tem certeza que deseja remover esta conexão? O card conectado não será excluído, apenas a relação.')) return;
    
    try {
      const headers = getAuthHeaders();
      const res = await fetch(`${apiUrl(`/cards-conectados/relacoes/${relacaoId}`)}?${getAuthParams()}`, {
        method: 'DELETE',
        headers
      });
      
      const data = await res.json();
      if (data.success) {
        success('Relação removida!');
        carregarDados();
      } else {
        toastError(data.error || 'Erro ao remover');
      }
    } catch (err) {
      toastError('Erro ao remover relação');
    }
  };

  const handleEditarConfig = (config: ConfiguracaoCardConectado) => {
    setEditingConfig(config);
    setFormData({
      nome: config.nome,
      descricao: config.descricao || '',
      tipo: config.tipo || 'criar',
      fluxoOrigemId: config.fluxoOrigemId,
      etapaOrigemId: config.etapaOrigemId,
      fluxoDestinoId: config.fluxoDestinoId,
      etapaDestinoId: config.etapaDestinoId,
      copiarHistorico: config.copiarHistorico,
      copiarAnexos: config.copiarAnexos,
      corDestaque: config.corDestaque,
      prefixoTitulo: config.prefixoTitulo || '[CONECTADO]',
      obrigarPreenchimento: config.obrigarPreenchimento || false,
      mapeamentoCampos: config.mapeamentoCampos || [],
      condicoes: (config.condicoes || []).map((c: any) => ({ campo: c.campo || '', operador: c.operador || 'igual', valor: c.valor || '' })),
      condicoesLogica: config.condicoesLogica || 'E',
      ativo: config.ativo
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      nome: '',
      descricao: '',
      tipo: 'criar',
      fluxoOrigemId: '',
      etapaOrigemId: '',
      fluxoDestinoId: '',
      etapaDestinoId: '',
      copiarHistorico: true,
      copiarAnexos: true,
      corDestaque: '#8B5CF6',
      prefixoTitulo: '[CONECTADO]',
      obrigarPreenchimento: false,
      mapeamentoCampos: [],
      condicoes: [],
      condicoesLogica: 'E',
      ativo: true
    });
    setEditingConfig(null);
    setShowForm(false);
  };

  // Campos do mapeamento
  const adicionarMapeamento = () => {
    setFormData(prev => ({
      ...prev,
      mapeamentoCampos: [...prev.mapeamentoCampos, { campoOrigemId: '', campoDestinoId: '' }]
    }));
  };

  const removerMapeamento = (index: number) => {
    setFormData(prev => ({
      ...prev,
      mapeamentoCampos: prev.mapeamentoCampos.filter((_, i) => i !== index)
    }));
  };

  const atualizarMapeamento = (index: number, campo: 'campoOrigemId' | 'campoDestinoId', valor: string) => {
    setFormData(prev => ({
      ...prev,
      mapeamentoCampos: prev.mapeamentoCampos.map((m, i) => 
        i === index ? { ...m, [campo]: valor } : m
      )
    }));
  };

  // Condições
  const adicionarCondicao = () => {
    setFormData(prev => ({
      ...prev,
      condicoes: [...prev.condicoes, { campo: '', operador: 'igual', valor: '' }]
    }));
  };

  const removerCondicao = (index: number) => {
    setFormData(prev => ({
      ...prev,
      condicoes: prev.condicoes.filter((_, i) => i !== index)
    }));
  };

  const atualizarCondicao = (index: number, chave: 'campo' | 'operador' | 'valor', valor: string) => {
    setFormData(prev => ({
      ...prev,
      condicoes: prev.condicoes.map((c, i) => 
        i === index ? { ...c, [chave]: valor } : c
      )
    }));
  };

  // Gerar lista de campos disponíveis no fluxo de origem para condições
  const getCamposOrigemParaCondicao = () => {
    const campos: { id: string; nome: string; grupo: string }[] = [
      { id: '__responsavel__', nome: 'Responsável do card', grupo: 'Card' },
      { id: '__criador__', nome: 'Criador do card', grupo: 'Card' },
      { id: '__titulo__', nome: 'Título do card', grupo: 'Card' },
    ];
    getEtapasDoFluxo(formData.fluxoOrigemId).forEach(etapa => {
      (etapa.campos || []).forEach(c => {
        campos.push({ id: c.id, nome: c.nome, grupo: etapa.nome });
      });
    });
    return campos;
  };

  const OPERADORES_CONDICAO = [
    { valor: 'igual', nome: 'Igual a' },
    { valor: 'diferente', nome: 'Diferente de' },
    { valor: 'contem', nome: 'Contém' },
    { valor: 'nao_contem', nome: 'Não contém' },
    { valor: 'vazio', nome: 'Está vazio' },
    { valor: 'nao_vazio', nome: 'Não está vazio' },
  ];

  // Agrupamento para visão de subprocessos
  const relacoesAgrupadas = relacoes.reduce((acc, rel) => {
    const key = rel.flowOrigemId;
    if (!acc[key]) acc[key] = [];
    acc[key].push(rel);
    return acc;
  }, {} as Record<string, CardConectadoRelacao[]>);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        <span className="ml-3 text-gray-500">Carregando cards conectados...</span>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 bg-indigo-50 rounded-lg">
            <LinkIcon className="w-6 h-6 text-indigo-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Cards Conectados</h1>
            <p className="text-sm text-gray-500">
              Configure subprocessos que geram cards conectados entre fluxos
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-gray-100 p-1 rounded-lg w-fit">
        <button
          onClick={() => setActiveTab('configuracoes')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
            activeTab === 'configuracoes' 
              ? 'bg-white text-indigo-700 shadow-sm' 
              : 'text-gray-600 hover:text-gray-800'
          }`}
        >
          <Cog6ToothIcon className="w-4 h-4 inline mr-1.5 -mt-0.5" />
          Configurações ({configuracoes.length})
        </button>
        <button
          onClick={() => setActiveTab('relacoes')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
            activeTab === 'relacoes' 
              ? 'bg-white text-indigo-700 shadow-sm' 
              : 'text-gray-600 hover:text-gray-800'
          }`}
        >
          <ArrowsRightLeftIcon className="w-4 h-4 inline mr-1.5 -mt-0.5" />
          Conexões Ativas ({relacoes.length})
        </button>
        <button
          onClick={() => setActiveTab('visao')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
            activeTab === 'visao' 
              ? 'bg-white text-indigo-700 shadow-sm' 
              : 'text-gray-600 hover:text-gray-800'
          }`}
        >
          <EyeIcon className="w-4 h-4 inline mr-1.5 -mt-0.5" />
          Visão Subprocessos
        </button>
      </div>

      {/* Tab: Configurações */}
      {activeTab === 'configuracoes' && (
        <div>
          {/* Botão Criar */}
          {!showForm && (
            <button
              onClick={() => { resetForm(); setShowForm(true); }}
              className="mb-6 flex items-center gap-2 px-4 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors shadow-sm"
            >
              <PlusIcon className="w-5 h-5" />
              Nova Configuração
            </button>
          )}

          {/* Formulário */}
          {showForm && (
            <div className="mb-6 bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <LinkIcon className="w-5 h-5 text-indigo-600" />
                {editingConfig ? 'Editar Configuração' : 'Nova Configuração de Card Conectado'}
              </h3>

              {/* Tipo de configuração */}
              <div className="flex gap-2 mb-4 bg-gray-100 p-1 rounded-lg w-fit">
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, tipo: 'criar' }))}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-2 ${
                    formData.tipo === 'criar'
                      ? 'bg-white text-indigo-700 shadow-sm'
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  <PlusIcon className="w-4 h-4" />
                  Criar Novo Card
                </button>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, tipo: 'mover_existente' }))}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-2 ${
                    formData.tipo === 'mover_existente'
                      ? 'bg-white text-indigo-700 shadow-sm'
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  <ArrowsRightLeftIcon className="w-4 h-4" />
                  Mover Card Existente
                </button>
              </div>

              {formData.tipo === 'mover_existente' && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-sm text-amber-800">
                  <strong>Mover Card Existente:</strong> Ao invés de criar um novo card, esta configuração vai buscar um card que já foi conectado anteriormente 
                  e movê-lo para o fluxo/etapa de destino, carregando os dados do subprocesso.
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                {/* Nome */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
                  <input
                    type="text"
                    value={formData.nome}
                    onChange={e => setFormData(prev => ({ ...prev, nome: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="Ex: Gerar OS de manutenção"
                  />
                </div>

                {/* Descrição */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Descrição</label>
                  <input
                    type="text"
                    value={formData.descricao}
                    onChange={e => setFormData(prev => ({ ...prev, descricao: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="Descrição da configuração"
                  />
                </div>
              </div>

              {/* Origem */}
              <div className="bg-indigo-50/60 border border-indigo-100 rounded-lg p-4 mb-4">
                <h4 className="text-sm font-semibold text-indigo-800 mb-3 flex items-center gap-2">
                  <DocumentDuplicateIcon className="w-4 h-4" />
                  Origem (quando o card estiver nesta etapa)
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Fluxo de Origem *</label>
                    <select
                      value={formData.fluxoOrigemId}
                      onChange={e => setFormData(prev => ({ ...prev, fluxoOrigemId: e.target.value, etapaOrigemId: '' }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="">Selecione o fluxo...</option>
                      {flows.map(f => (
                        <option key={f.id} value={f.id}>{f.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Etapa de Origem *</label>
                    <select
                      value={formData.etapaOrigemId}
                      onChange={e => setFormData(prev => ({ ...prev, etapaOrigemId: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                      disabled={!formData.fluxoOrigemId}
                    >
                      <option value="">Selecione a etapa...</option>
                      {getEtapasDoFluxo(formData.fluxoOrigemId).map(e => (
                        <option key={e.id} value={e.id}>{e.nome}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Destino */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <ArrowRightIcon className="w-4 h-4" />
                  {formData.tipo === 'mover_existente' 
                    ? 'Destino (para onde o card existente será movido)' 
                    : 'Destino (onde o card conectado será criado)'}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Fluxo de Destino *</label>
                    <select
                      value={formData.fluxoDestinoId}
                      onChange={e => setFormData(prev => ({ ...prev, fluxoDestinoId: e.target.value, etapaDestinoId: '' }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="">Selecione o fluxo...</option>
                      {flows.map(f => (
                        <option key={f.id} value={f.id}>{f.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Etapa de Destino *</label>
                    <select
                      value={formData.etapaDestinoId}
                      onChange={e => setFormData(prev => ({ ...prev, etapaDestinoId: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                      disabled={!formData.fluxoDestinoId}
                    >
                      <option value="">Selecione a etapa...</option>
                      {getEtapasDoFluxo(formData.fluxoDestinoId).map(e => (
                        <option key={e.id} value={e.id}>{e.nome}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Opções */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Opções</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.copiarHistorico}
                      onChange={e => setFormData(prev => ({ ...prev, copiarHistorico: e.target.checked }))}
                      className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
                    />
                    <span className="text-sm text-gray-700">Copiar histórico/timeline</span>
                  </label>
                  
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.copiarAnexos}
                      onChange={e => setFormData(prev => ({ ...prev, copiarAnexos: e.target.checked }))}
                      className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
                    />
                    <span className="text-sm text-gray-700">Copiar anexos</span>
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer" title="Ao criar, abrir o card para preencher os campos obrigatórios da etapa de destino">
                    <input
                      type="checkbox"
                      checked={formData.obrigarPreenchimento}
                      onChange={e => setFormData(prev => ({ ...prev, obrigarPreenchimento: e.target.checked }))}
                      className="w-4 h-4 text-orange-600 rounded focus:ring-orange-500"
                    />
                    <span className="text-sm text-gray-700">Obrigar preenchimento</span>
                  </label>

                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Prefixo do título</label>
                    <input
                      type="text"
                      value={formData.prefixoTitulo}
                      onChange={e => setFormData(prev => ({ ...prev, prefixoTitulo: e.target.value }))}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                      placeholder="[CONECTADO]"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Cor de destaque</label>
                    <div className="flex gap-1.5 flex-wrap">
                      {CORES_DESTAQUE.map(cor => (
                        <button
                          key={cor.valor}
                          onClick={() => setFormData(prev => ({ ...prev, corDestaque: cor.valor }))}
                          className={`w-7 h-7 rounded-full border-2 transition-all ${
                            formData.corDestaque === cor.valor 
                              ? 'border-gray-800 scale-110 shadow-md' 
                              : 'border-transparent hover:border-gray-400'
                          }`}
                          style={{ backgroundColor: cor.valor }}
                          title={cor.nome}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Condições / Filtros */}
              <div className="bg-violet-50 border border-violet-200 rounded-lg p-4 mb-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h4 className="text-sm font-semibold text-violet-800 flex items-center gap-2">
                      <FunnelIcon className="w-4 h-4" />
                      Condições / Filtros (opcional)
                    </h4>
                    <p className="text-xs text-violet-600 mt-0.5">
                      Defina quando o card conectado deve ser criado. Sem condições = sempre cria.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={adicionarCondicao}
                    className="text-xs px-2 py-1 bg-violet-200 text-violet-800 rounded hover:bg-violet-300 transition-colors"
                  >
                    + Adicionar Condição
                  </button>
                </div>
                
                {formData.condicoes.length === 0 ? (
                  <p className="text-xs text-violet-500 italic">
                    Nenhuma condição definida. O card conectado será criado para todos os cards que entrarem na etapa de origem.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {/* Lógica E/OU */}
                    {formData.condicoes.length > 1 && (
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs text-violet-700 font-medium">Combinar condições com:</span>
                        <div className="flex bg-violet-100 rounded-md overflow-hidden">
                          <button
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, condicoesLogica: 'E' }))}
                            className={`px-3 py-1 text-xs font-medium transition-all ${
                              formData.condicoesLogica === 'E'
                                ? 'bg-violet-600 text-white'
                                : 'text-violet-700 hover:bg-violet-200'
                            }`}
                          >
                            E (todas)
                          </button>
                          <button
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, condicoesLogica: 'OU' }))}
                            className={`px-3 py-1 text-xs font-medium transition-all ${
                              formData.condicoesLogica === 'OU'
                                ? 'bg-violet-600 text-white'
                                : 'text-violet-700 hover:bg-violet-200'
                            }`}
                          >
                            OU (qualquer)
                          </button>
                        </div>
                      </div>
                    )}

                    {formData.condicoes.map((cond, index) => (
                      <div key={index} className="flex items-center gap-2 bg-white/60 rounded-lg p-2 border border-violet-100">
                        {/* Se tem mais de uma condição, mostrar indicador E/OU */}
                        {index > 0 && (
                          <span className="text-[10px] font-bold text-violet-500 bg-violet-100 px-1.5 py-0.5 rounded flex-shrink-0">
                            {formData.condicoesLogica}
                          </span>
                        )}
                        
                        {/* Campo */}
                        <select
                          value={cond.campo}
                          onChange={e => atualizarCondicao(index, 'campo', e.target.value)}
                          className="flex-1 min-w-[140px] px-2 py-1.5 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-violet-500"
                        >
                          <option value="">Selecione o campo...</option>
                          <optgroup label="Card">
                            <option value="__responsavel__">Responsável do card</option>
                            <option value="__criador__">Criador do card</option>
                            <option value="__titulo__">Título do card</option>
                          </optgroup>
                          {formData.fluxoOrigemId && getEtapasDoFluxo(formData.fluxoOrigemId).map(etapa => (
                            <optgroup key={etapa.id} label={etapa.nome}>
                              {(etapa.campos || []).map(c => (
                                <option key={c.id} value={c.id}>{c.nome}</option>
                              ))}
                            </optgroup>
                          ))}
                        </select>

                        {/* Operador */}
                        <select
                          value={cond.operador}
                          onChange={e => atualizarCondicao(index, 'operador', e.target.value)}
                          className="w-[130px] px-2 py-1.5 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-violet-500"
                        >
                          {OPERADORES_CONDICAO.map(op => (
                            <option key={op.valor} value={op.valor}>{op.nome}</option>
                          ))}
                        </select>

                        {/* Valor (oculto para operadores vazio/nao_vazio) */}
                        {cond.operador !== 'vazio' && cond.operador !== 'nao_vazio' && (
                          <input
                            type="text"
                            value={cond.valor}
                            onChange={e => atualizarCondicao(index, 'valor', e.target.value)}
                            className="flex-1 min-w-[120px] px-2 py-1.5 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-violet-500"
                            placeholder="Valor para comparar..."
                          />
                        )}

                        {/* Remover */}
                        <button
                          type="button"
                          onClick={() => removerCondicao(index)}
                          className="p-1 text-red-500 hover:bg-red-50 rounded flex-shrink-0"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Mapeamento de Campos */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-amber-800 flex items-center gap-2">
                    <ClipboardDocumentListIcon className="w-4 h-4" />
                    Mapeamento de Campos (opcional)
                  </h4>
                  <button
                    onClick={adicionarMapeamento}
                    className="text-xs px-2 py-1 bg-amber-200 text-amber-800 rounded hover:bg-amber-300 transition-colors"
                  >
                    + Adicionar Campo
                  </button>
                </div>
                
                {formData.mapeamentoCampos.length === 0 ? (
                  <p className="text-xs text-amber-600 italic">
                    Nenhum mapeamento definido. Os campos serão copiados automaticamente se tiverem o mesmo ID.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {formData.mapeamentoCampos.map((mapa, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <select
                          value={mapa.campoOrigemId}
                          onChange={e => atualizarMapeamento(index, 'campoOrigemId', e.target.value)}
                          className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-xs"
                        >
                          <option value="">Campo de origem...</option>
                          {getEtapasDoFluxo(formData.fluxoOrigemId).flatMap(e => 
                            (e.campos || []).map(c => (
                              <option key={`${e.id}-${c.id}`} value={c.id}>
                                {e.nome} → {c.nome}
                              </option>
                            ))
                          )}
                        </select>
                        <ArrowRightIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <select
                          value={mapa.campoDestinoId}
                          onChange={e => atualizarMapeamento(index, 'campoDestinoId', e.target.value)}
                          className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-xs"
                        >
                          <option value="">Campo de destino...</option>
                          {getEtapasDoFluxo(formData.fluxoDestinoId).flatMap(e => 
                            (e.campos || []).map(c => (
                              <option key={`${e.id}-${c.id}`} value={c.id}>
                                {e.nome} → {c.nome}
                              </option>
                            ))
                          )}
                        </select>
                        <button
                          onClick={() => removerMapeamento(index)}
                          className="p-1 text-red-500 hover:bg-red-50 rounded"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Botões */}
              <div className="flex gap-3">
                <button
                  onClick={handleSalvarConfig}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium"
                >
                  {editingConfig ? 'Salvar Alterações' : 'Criar Configuração'}
                </button>
                <button
                  onClick={resetForm}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm"
                >
                  Cancelar
                </button>
              </div>
            </div>
          )}

          {/* Lista de Configurações */}
          {configuracoes.length === 0 && !showForm ? (
            <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
              <LinkIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <h3 className="text-lg font-medium text-gray-600 mb-1">Nenhuma configuração</h3>
              <p className="text-sm text-gray-400 mb-4">
                Crie sua primeira configuração de card conectado para gerar subprocessos entre fluxos.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {configuracoes.map(config => (
                <div 
                  key={config.id}
                  className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3 flex-1">
                      {/* Cor de destaque */}
                      <div 
                        className="w-3 h-full min-h-[60px] rounded-full flex-shrink-0"
                        style={{ backgroundColor: config.corDestaque }}
                      />
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-semibold text-gray-800">{config.nome}</h4>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            config.ativo 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-gray-100 text-gray-500'
                          }`}>
                            {config.ativo ? 'Ativo' : 'Inativo'}
                          </span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            config.tipo === 'mover_existente'
                              ? 'bg-amber-100 text-amber-700'
                              : 'bg-indigo-50 text-indigo-600'
                          }`}>
                            {config.tipo === 'mover_existente' ? '↔ Mover Existente' : '+ Criar Card'}
                          </span>
                        </div>
                        
                        {config.descricao && (
                          <p className="text-xs text-gray-500 mb-2">{config.descricao}</p>
                        )}
                        
                        {/* Fluxo visual */}
                        <div className="flex items-center gap-2 text-xs">
                          <div className="bg-indigo-50 border border-indigo-200 px-2.5 py-1 rounded-md">
                            <span className="text-indigo-800 font-medium">{getFlowNome(config.fluxoOrigemId)}</span>
                            <span className="text-indigo-400 mx-1">→</span>
                            <span className="text-indigo-600">{getEtapaNome(config.fluxoOrigemId, config.etapaOrigemId)}</span>
                          </div>
                          
                          <ArrowRightIcon className="w-4 h-4 text-indigo-500 flex-shrink-0" />
                          
                          <div className="bg-gray-50 border border-gray-200 px-2.5 py-1 rounded-md">
                            <span className="text-gray-800 font-medium">{getFlowNome(config.fluxoDestinoId)}</span>
                            <span className="text-gray-400 mx-1">→</span>
                            <span className="text-gray-600">{getEtapaNome(config.fluxoDestinoId, config.etapaDestinoId)}</span>
                          </div>
                        </div>

                        {/* Badges opcionais */}
                        <div className="flex gap-2 mt-2">
                          {config.copiarHistorico && (
                            <span className="text-[10px] px-1.5 py-0.5 bg-indigo-50 text-indigo-600 rounded">
                              📋 Copia Histórico
                            </span>
                          )}
                          {config.copiarAnexos && (
                            <span className="text-[10px] px-1.5 py-0.5 bg-indigo-50 text-indigo-600 rounded">
                              📎 Copia Anexos
                            </span>
                          )}
                          {config.prefixoTitulo && (
                            <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded">
                              Prefixo: {config.prefixoTitulo}
                            </span>
                          )}
                          {config.condicoes && config.condicoes.length > 0 && (
                            <span className="text-[10px] px-1.5 py-0.5 bg-violet-50 text-violet-600 rounded flex items-center gap-1">
                              🔍 {config.condicoes.length} condição(ões) ({config.condicoesLogica || 'E'})
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Ações */}
                    <div className="flex items-center gap-1 ml-3">
                      <button
                        onClick={() => handleToggleConfig(config.id)}
                        className={`p-1.5 rounded-lg transition-colors ${
                          config.ativo 
                            ? 'text-green-600 hover:bg-green-50' 
                            : 'text-gray-400 hover:bg-gray-50'
                        }`}
                        title={config.ativo ? 'Desativar' : 'Ativar'}
                      >
                        {config.ativo ? <CheckCircleIcon className="w-5 h-5" /> : <XCircleIcon className="w-5 h-5" />}
                      </button>
                      <button
                        onClick={() => handleEditarConfig(config)}
                        className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Editar"
                      >
                        <PencilIcon className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDeletarConfig(config.id)}
                        className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        title="Excluir"
                      >
                        <TrashIcon className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Tab: Conexões Ativas (Relações) */}
      {activeTab === 'relacoes' && (
        <div>
          {relacoes.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
              <ArrowsRightLeftIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <h3 className="text-lg font-medium text-gray-600 mb-1">Nenhuma conexão ativa</h3>
              <p className="text-sm text-gray-400">
                As conexões aparecerão aqui quando cards conectados forem criados.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {relacoes.map(rel => (
                <div 
                  key={rel.id}
                  className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-md transition-shadow"
                >
                  <div 
                    className="flex items-center justify-between p-4 cursor-pointer"
                    onClick={() => setExpandedRelacao(expandedRelacao === rel.id ? null : rel.id)}
                  >
                    <div className="flex items-center gap-3 flex-1">
                      <div className="w-2 h-10 bg-indigo-500 rounded-full flex-shrink-0" />
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 text-sm">
                          <span className="font-medium text-gray-800 truncate">{rel.cardOrigemTitulo}</span>
                          <ArrowRightIcon className="w-4 h-4 text-indigo-500 flex-shrink-0" />
                          <span className="font-medium text-indigo-700 truncate">{rel.cardDestinoTitulo}</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                          <span>{rel.flowOrigemNome}</span>
                          <span>→</span>
                          <span>{rel.flowDestinoNome}</span>
                          <span className="text-gray-300">|</span>
                          <span>{new Date(rel.createdAt).toLocaleDateString('pt-BR')}</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDeletarRelacao(rel.id); }}
                        className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg"
                        title="Remover conexão"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                      {expandedRelacao === rel.id 
                        ? <ChevronUpIcon className="w-4 h-4 text-gray-400" />
                        : <ChevronDownIcon className="w-4 h-4 text-gray-400" />
                      }
                    </div>
                  </div>

                  {expandedRelacao === rel.id && (
                    <div className="px-4 pb-4 border-t border-gray-100 pt-3">
                      <div className="grid grid-cols-2 gap-4 text-xs">
                        <div className="bg-indigo-50 p-3 rounded-lg">
                          <h5 className="font-semibold text-indigo-800 mb-1">Card de Origem</h5>
                          <p><span className="text-gray-500">Card:</span> {rel.cardOrigemTitulo}</p>
                          <p><span className="text-gray-500">Fluxo:</span> {rel.flowOrigemNome}</p>
                          <p><span className="text-gray-500">Etapa:</span> {rel.etapaOrigemNome}</p>
                        </div>
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <h5 className="font-semibold text-gray-800 mb-1">Card Conectado</h5>
                          <p><span className="text-gray-500">Card:</span> {rel.cardDestinoTitulo}</p>
                          <p><span className="text-gray-500">Fluxo:</span> {rel.flowDestinoNome}</p>
                          <p><span className="text-gray-500">Etapa:</span> {rel.etapaDestinoNome}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Tab: Visão de Subprocessos */}
      {activeTab === 'visao' && (
        <div>
          {Object.keys(relacoesAgrupadas).length === 0 ? (
            <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
              <EyeIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <h3 className="text-lg font-medium text-gray-600 mb-1">Nenhum subprocesso</h3>
              <p className="text-sm text-gray-400">
                A visualização de subprocessos aparecerá quando houver cards conectados entre fluxos.
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(relacoesAgrupadas).map(([flowId, rels]) => {
                const flowNome = rels[0]?.flowOrigemNome || getFlowNome(flowId);
                
                // Agrupar por fluxo de destino
                const porDestino = rels.reduce((acc, r) => {
                  if (!acc[r.flowDestinoId]) acc[r.flowDestinoId] = [];
                  acc[r.flowDestinoId].push(r);
                  return acc;
                }, {} as Record<string, CardConectadoRelacao[]>);
                
                return (
                  <div key={flowId} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                    {/* Header do fluxo de origem */}
                    <div className="bg-gray-50 px-5 py-3 border-b border-gray-200">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-indigo-500 rounded-full" />
                        <h3 className="font-semibold text-gray-800">{flowNome}</h3>
                        <span className="text-xs bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded-full">
                          {rels.length} card{rels.length > 1 ? 's' : ''} conectado{rels.length > 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>
                    
                    <div className="p-4">
                      {Object.entries(porDestino).map(([destFlowId, destRels]) => {
                        const destFlowNome = destRels[0]?.flowDestinoNome || getFlowNome(destFlowId);
                        
                        return (
                          <div key={destFlowId} className="mb-4 last:mb-0">
                            {/* Subprocesso */}
                            <div className="flex items-center gap-2 mb-2">
                              <ArrowRightIcon className="w-4 h-4 text-indigo-500" />
                              <div className="flex items-center gap-2">
                                <div className="w-2.5 h-2.5 bg-indigo-400 rounded-full" />
                                <span className="text-sm font-medium text-gray-700">{destFlowNome}</span>
                                <span className="text-xs bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded">
                                  {destRels.length}
                                </span>
                              </div>
                            </div>
                            
                            {/* Cards conectados */}
                            <div className="ml-6 space-y-1.5">
                              {destRels.map(rel => (
                                <div key={rel.id} className="flex items-center gap-2 text-xs bg-gray-50 px-3 py-2 rounded-lg">
                                  <div className="w-1.5 h-1.5 bg-indigo-400 rounded-full" />
                                  <span className="text-gray-600">{rel.cardOrigemTitulo}</span>
                                  <ArrowRightIcon className="w-3 h-3 text-gray-400" />
                                  <span className="text-indigo-700 font-medium">{rel.cardDestinoTitulo}</span>
                                  <span className="text-gray-400 ml-auto">
                                    {new Date(rel.createdAt).toLocaleDateString('pt-BR')}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CardsConectados;
